import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    Paper,
    Stack,
    Button,
    Avatar,
    Divider,
    IconButton,
    Chip,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    Alert,
    Snackbar,
    MenuItem,
    TextField
} from '@mui/material';
import { ArrowBack, Phone, Room, Directions, InfoOutlined, LocalShipping } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrderDetails, useUpdateOrderStatus, useAvailableRiders, useVendorAssignRider, useVendorRiderLocation, useVendorNotifyRider } from 'hooks/useOrder';
import { OrderInvoice } from 'views/vendor/component/OrderInvoice';
import { useReactToPrint } from 'react-to-print';
import { useSocket } from 'contexts/SocketContext';
import RiderTrackerMap from 'views/common/RiderTrackerMap';

const orderSteps = ['pending', 'preparing', 'prepared', 'dispatched', 'delivered'];

export default function VendorOrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    // UI State
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [selectedStatus, setSelectedStatus] = useState('');
    const [cancelReason, setCancelReason] = useState('');
    const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);

    // Data Hooks
    const { data: orderRes, isLoading, error } = useOrderDetails(id);
    const { mutate: updateStatus, isPending: isUpdating } = useUpdateOrderStatus();
    
    // Rider Assignment Hooks
    const { data: ridersRes, isLoading: isLoadingRiders } = useAvailableRiders(id);
    const { mutate: assignRider, isPending: isAssigning } = useVendorAssignRider();
    const { mutate: notifyRider, isPending: isNotifying } = useVendorNotifyRider();
    
    const [selectedRider, setSelectedRider] = useState('');

    const invoiceRef = React.useRef();
    const handlePrint = useReactToPrint({
        contentRef: invoiceRef
    });

    const order = orderRes?.data;

    const { socket } = useSocket();
    const hasRider = !!(order?.riderDetails?.id || order?.rider);
    const { data: initialRiderLocRes } = useVendorRiderLocation(hasRider ? id : null);
    const [riderCoords, setRiderCoords] = useState(null);

    // Initial rider location setup
    useEffect(() => {
        if (initialRiderLocRes?.success && initialRiderLocRes.data?.location?.coordinates) {
            const [lng, lat] = initialRiderLocRes.data.location.coordinates;
            setRiderCoords([Number(lat), Number(lng)]);
        }
    }, [initialRiderLocRes]);

    // Live socket rider location tracking
    useEffect(() => {
        if (socket && id && order) {
            const riderId = order.riderDetails?.id || (order.rider && typeof order.rider === 'object' ? order.rider._id || order.rider.id : order.rider);
            
            const handleRiderLocationUpdate = (data) => {
                if (data.orderId === id || data.riderId === riderId) {
                    console.log('Socket rider location update:', data);
                    if (data.coordinates && data.coordinates.length === 2) {
                        const [lng, lat] = data.coordinates;
                        setRiderCoords([Number(lat), Number(lng)]);
                    }
                }
            };

            socket.on('rider_location_updated', handleRiderLocationUpdate);

            return () => {
                socket.off('rider_location_updated', handleRiderLocationUpdate);
            };
        }
    }, [socket, id, order]);

    // Standardize the final status check
    const isFinalized = order?.status === 'delivered' || order?.status === 'cancelled' || order?.status === 'canceled';

    useEffect(() => {
        if (order?.status) setSelectedStatus(order.status);
    }, [order?.status]);

    const handleUpdate = () => {
        // 1. Guard against double-clicks and finalized orders
        if (isLocallySubmitting || isUpdating || isFinalized) return;

        // 2. Validation for cancellation
        if (selectedStatus === 'cancelled' && !cancelReason.trim()) {
            setToast({ open: true, message: 'Please provide a reason for cancellation', severity: 'warning' });
            return;
        }

        // 3. Prevent submission if no change made (unless it's a cancellation attempt)
        if (selectedStatus === order?.status) return;

        setIsLocallySubmitting(true);

        const payload = {
            orderId: id,
            status: selectedStatus,
            ...(selectedStatus === 'cancelled' && { reason: cancelReason })
        };

        updateStatus(payload, {
            onSuccess: () => {
                setToast({ open: true, message: `Order updated to ${selectedStatus}`, severity: 'success' });
                setIsLocallySubmitting(false);
                if (selectedStatus === 'cancelled') setCancelReason('');
            },
            onError: (err) => {
                setIsLocallySubmitting(false);
                const errorMsg = err?.response?.data?.message || err?.message || 'Update failed';
                setToast({ open: true, message: errorMsg, severity: 'error' });
            }
        });
    };

    const handleAssignRider = () => {
        if (!selectedRider) return;
        assignRider({ orderId: id, riderId: selectedRider }, {
            onSuccess: () => {
                setToast({ open: true, message: 'Rider assigned successfully', severity: 'success' });
                setSelectedRider('');
            },
            onError: (err) => {
                const errorMsg = err?.response?.data?.message || err?.message || 'Failed to assign rider';
                setToast({ open: true, message: errorMsg, severity: 'error' });
            }
        });
    };

    const handleNotifyRider = () => {
        notifyRider({ orderId: id }, {
            onSuccess: () => {
                setToast({ open: true, message: 'Rider notified successfully!', severity: 'success' });
            },
            onError: (err) => {
                const errorMsg = err?.response?.data?.message || err?.message || 'Failed to notify rider';
                setToast({ open: true, message: errorMsg, severity: 'error' });
            }
        });
    };

    if (isLoading)
        return (
            <Box p={10} textAlign="center">
                <CircularProgress />
            </Box>
        );

    if (error || !order)
        return (
            <Box p={4}>
                <Alert severity="error">Order not found or API Error</Alert>
            </Box>
        );

    const currentStepIndex = orderSteps.indexOf(order.status);

    // Unpack coordinates from order object
    const getCoordinates = (loc) => {
        if (loc && Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
            const [lng, lat] = loc.coordinates;
            if (lat && lng) return [Number(lat), Number(lng)];
        }
        return null;
    };

    const pickupCoords = getCoordinates(order?.pickup) || 
                         (Array.isArray(order?.pickupLocation) && order?.pickupLocation.length === 2 
                            ? [order.pickupLocation[1], order.pickupLocation[0]] 
                            : null) ||
                         (order?.vendor?.location?.coordinates && order?.vendor.location.coordinates.length === 2
                            ? [order.vendor.location.coordinates[1], order.vendor.location.coordinates[0]]
                            : null);

    const destCoords = getCoordinates(order?.destination) ||
                       (Array.isArray(order?.deliveryLocation) && order?.deliveryLocation.length === 2
                          ? [order.deliveryLocation[1], order.deliveryLocation[0]]
                          : null);

    return (
        <Box p={{ xs: 2, md: 4 }} maxWidth="lg" margin="auto">
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>

            {/* Header */}
            <Stack direction="row" justifyContent="space-between" mb={4}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            Order #{order.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ID: {order._id} • {new Date(order.createdAt).toLocaleString()}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<InfoOutlined />} onClick={() => handlePrint()}>
                        Print Invoice
                    </Button>
                </Stack>
                {/* Hidden container for printing */}
                <Box sx={{ display: 'none', displayPrint: 'block' }}>
                    <div ref={invoiceRef}>
                        <OrderInvoice order={order} />
                    </div>
                </Box>
            </Stack>

            {/* Progress Stepper */}
            <Paper sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Stepper activeStep={currentStepIndex} alternativeLabel>
                    {orderSteps.map((label) => (
                        <Step key={label}>
                            <StepLabel>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                                    {label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    {/* Items List */}
                    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>
                            Items Summary
                        </Typography>
                        <Stack spacing={3}>
                            {order.products?.map((item, idx) => (
                                <Box key={item._id}>
                                    <Stack direction="row" spacing={2} justifyContent="space-between">
                                        <Stack direction="row" spacing={2}>
                                            <Avatar
                                                src={item.product?.thumbnail}
                                                variant="rounded"
                                                sx={{ width: 70, height: 70, bgcolor: 'grey.100' }}
                                            />
                                            <Box>
                                                <Typography fontWeight={700}>{item.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Quantity: {item.quantity}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Typography fontWeight={800}>₦{(item.price * item.quantity).toLocaleString()}</Typography>
                                    </Stack>
                                    {idx < order.products.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </Box>
                            ))}
                        </Stack>

                        <Box mt={4} p={3} sx={{ bgcolor: '#fafafa', borderRadius: 2 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight={700} mb={2}>
                                Payout Information
                            </Typography>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="text.secondary">Order Subtotal</Typography>
                                    <Typography fontWeight={600}>₦{order.amount.toLocaleString()}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="text.secondary">Platform Commission ({order.commission}%)</Typography>
                                    <Typography fontWeight={600} color="error">
                                        -₦{((order.amount * (order.commission || 0)) / 100).toLocaleString()}
                                    </Typography>
                                </Stack>
                                <Divider sx={{ my: 1 }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="h5" fontWeight={800}>
                                        Net Earnings
                                    </Typography>
                                    <Typography variant="h5" fontWeight={800} color="success.main">
                                        ₦{(order.amount - (order.amount * (order.commission || 0)) / 100).toLocaleString()}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    * This is the amount that will be settled to your wallet upon completion.
                                </Typography>
                            </Stack>
                        </Box>
                    </Paper>

                    {/* Logistics Section */}
                    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                        <Typography variant="h6" fontWeight={700} mb={3}>
                            Logistics Details
                        </Typography>
                        <Grid container spacing={3} mb={pickupCoords || destCoords ? 3 : 0}>
                            <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} mb={1}>
                                    <Room color="primary" />
                                    <Typography fontWeight={700}>Pickup</Typography>
                                </Stack>
                                <Typography variant="body2">{order.pickup?.street || order.pickupAddress}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1} mb={1}>
                                    <Directions color="success" />
                                    <Typography fontWeight={700}>Destination</Typography>
                                </Stack>
                                <Typography variant="body2">{order.destination?.street || order.deliveryAddress}</Typography>
                            </Grid>
                        </Grid>

                        {/* 🗺️ Live Rider Tracking Map */}
                        {(pickupCoords || destCoords) && (
                            <Box mt={2}>
                                <RiderTrackerMap
                                    pickup={pickupCoords}
                                    destination={destCoords}
                                    rider={riderCoords}
                                    height={350}
                                />
                            </Box>
                        )}
                    </Paper>

                    {/* 📦 Package Specifications (New section) */}
                    {order.orderType === 'package' && order.package && (
                        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid #e0e0e0' }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>
                                Package Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={3}>
                                    <Typography variant="caption" color="text.secondary">
                                        TYPE
                                    </Typography>
                                    <Typography fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                                        {order.package.packageType || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="caption" color="text.secondary">
                                        WEIGHT
                                    </Typography>
                                    <Typography fontWeight={600}>{order.package.weight || 'N/A'} kg</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="caption" color="text.secondary">
                                        SIZE
                                    </Typography>
                                    <Typography fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                                        {order.package.size || 'Standard'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="caption" color="text.secondary">
                                        FRAGILE
                                    </Typography>
                                    <Chip
                                        label={order.package.isFragile ? 'YES' : 'NO'}
                                        size="small"
                                        color={order.package.isFragile ? 'error' : 'default'}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        DESCRIPTION
                                    </Typography>
                                    <Typography variant="body2">{order.package.description || 'No description provided'}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* ✅ Proof of Delivery (New section) */}
                    {order.deliveryDetails && order.status === 'delivered' && (
                        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #4caf50' }}>
                            <Typography variant="h6" fontWeight={700} mb={3} color="success.main">
                                Proof of Delivery
                            </Typography>
                            <Grid container spacing={3}>
                                {order.deliveryDetails.deliveryImage && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                            PHOTOGRAPHIC EVIDENCE
                                        </Typography>
                                        <Box
                                            component="img"
                                            src={order.deliveryDetails.deliveryImage}
                                            sx={{
                                                width: '100%',
                                                borderRadius: 2,
                                                maxHeight: 300,
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Grid>
                                )}
                                {order.deliveryDetails.customerSignature && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                            CUSTOMER SIGNATURE
                                        </Typography>
                                        <Box
                                            component="img"
                                            src={order.deliveryDetails.customerSignature}
                                            sx={{
                                                width: '100%',
                                                borderRadius: 2,
                                                bgcolor: '#f9f9f9',
                                                p: 1
                                            }}
                                        />
                                    </Grid>
                                )}
                                {order.deliveryDetails.deliveryNotes && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">
                                            DELIVERY NOTES
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                            "{order.deliveryDetails.deliveryNotes}"
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    )}

                    {/* Customer's Note to Vendor */}
                    {order.vendorNote && (
                        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid #ffe0b2', bgcolor: '#fffde7' }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <InfoOutlined color="warning" />
                                <Typography variant="h6" fontWeight={700} color="warning.dark">
                                    Customer's Note to Vendor
                                </Typography>
                            </Stack>
                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#5d4037' }}>
                                "{order.vendorNote}"
                            </Typography>
                        </Paper>
                    )}
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={3}>
                        {/* Status Management - LOCKS UI IF FINALIZED */}
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'primary.light',
                                opacity: isFinalized ? 0.7 : 1,
                                pointerEvents: isFinalized ? 'none' : 'auto'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={700} mb={2}>
                                Update Progress
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={selectedStatus}
                                    disabled={isLocallySubmitting || isUpdating || isFinalized}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <MenuItem key="preparing" value="preparing">
                                        PREPARING
                                    </MenuItem>
                                    <MenuItem key="prepared" value="prepared">
                                        PREPARED
                                    </MenuItem>
                                    {/* Using double 'L' to match your Backend validation */}
                                    <MenuItem value="cancelled" sx={{ color: 'error.main' }}>
                                        CANCEL ORDER
                                    </MenuItem>
                                </TextField>

                                {selectedStatus === 'cancelled' && (
                                    <TextField
                                        label="Reason for cancellation"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        value={cancelReason}
                                        onChange={(e) => setCancelReason(e.target.value)}
                                        placeholder="Why is this order being cancelled?"
                                    />
                                )}

                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleUpdate}
                                    disabled={isLocallySubmitting || isUpdating || isFinalized || selectedStatus === order.status}
                                    sx={{ py: 1.5, fontWeight: 700 }}
                                >
                                    {isLocallySubmitting || isUpdating ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={18} color="inherit" />
                                            <span>Processing...</span>
                                        </Stack>
                                    ) : selectedStatus === 'cancelled' ? (
                                        'Confirm Cancellation'
                                    ) : selectedStatus === 'preparing' ? (
                                        'Start Preparing Order'
                                    ) : selectedStatus === 'prepared' ? (
                                        'Mark as Prepared'
                                    ) : (
                                        'Update Order'
                                    )}
                                </Button>
                            </Stack>
                        </Paper>

                        {/* Assign Rider Section (Visible when prepared/dispatched and not finalized) */}
                        {!isFinalized && order.status !== 'pending' && (
                            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} mb={2}>
                                    {hasRider ? 'Change Assigned Rider' : 'Assign a Rider'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                    {hasRider 
                                        ? 'The system has automatically assigned a rider. You can manually override and assign a different rider here.'
                                        : 'The system automatically assigns a rider when the order is prepared. You can manually override this here.'}
                                </Typography>
                                {!isLoadingRiders && (!ridersRes?.data || ridersRes.data.length === 0) && (
                                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                        No active riders are currently available in your area. Please check back later.
                                    </Alert>
                                )}
                                <Stack spacing={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        value={selectedRider}
                                        disabled={isLoadingRiders || isAssigning}
                                        onChange={(e) => setSelectedRider(e.target.value)}
                                        label={isLoadingRiders ? 'Loading riders...' : 'Select a rider'}
                                    >
                                        {ridersRes?.data?.map((rider) => (
                                            <MenuItem key={rider._id} value={rider._id}>
                                                {rider.name || 'Unnamed Rider'} - {rider.vehicleTypeId ? 'Vehicle' : 'Bike'} 
                                            </MenuItem>
                                        ))}
                                        {!isLoadingRiders && (!ridersRes?.data || ridersRes.data.length === 0) && (
                                            <MenuItem disabled value="">
                                                No available riders in your area
                                            </MenuItem>
                                        )}
                                    </TextField>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        disabled={!selectedRider || isAssigning}
                                        onClick={handleAssignRider}
                                    >
                                        {isAssigning ? 'Assigning...' : hasRider ? 'Re-assign Rider' : 'Manually Assign Rider'}
                                    </Button>
                                </Stack>
                            </Paper>
                        )}

                        {/* Rider Info (New Section) */}
                        {order.rider && (
                            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e3f2fd' }}>
                                <Stack direction="row" justifyContent="space-between" mb={2}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Assigned Rider
                                    </Typography>
                                    <LocalShipping color="primary" />
                                </Stack>
                                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                    <Avatar src={order.rider?.profilePicture} sx={{ bgcolor: 'info.main' }}>
                                        {order.rider?.firstName?.[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight={700}>
                                            {order.rider?.firstName} {order.rider?.lastName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {order.rider?.activeVehicle?.type?.name || 'Rider'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Divider sx={{ my: 2 }} />
                                <Stack spacing={2}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<Phone fontSize="small" />}
                                        href={`tel:${order.rider?.phoneNumber}`}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Call Rider
                                    </Button>
                                    {order.status === 'prepared' && !order.riderNotified && (
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNotifyRider}
                                            disabled={isNotifying}
                                            sx={{ py: 1.5, fontWeight: 700 }}
                                        >
                                            {isNotifying ? 'Notifying...' : 'Mark as Ready for Pickup'}
                                        </Button>
                                    )}
                                    {order.riderNotified && (
                                        <Alert severity="success" sx={{ py: 0, '& .MuiAlert-message': { py: 1 } }}>
                                            Rider has been notified
                                        </Alert>
                                    )}
                                </Stack>
                            </Paper>
                        )}

                        {/* Customer Info */}
                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Customer
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                <Avatar sx={{ bgcolor: 'secondary.main', color: 'white' }}>{order.user?.firstName?.[0]}</Avatar>
                                <Box>
                                    <Typography fontWeight={700}>
                                        {order.user?.firstName} {order.user?.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ID: {order.user?._id?.substring(0, 8)}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Divider sx={{ my: 2 }} />
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <Phone fontSize="small" color="action" />
                                <Typography variant="body2">{order.user?.phoneNumber}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <InfoOutlined fontSize="small" color="action" />
                                <Typography variant="body2">{order.user?.email}</Typography>
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}
